import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import type { Service, BreadcrumbList, WithContext } from 'schema-dts';

interface ServicePageSchemaProps {
  serviceName: string;
  serviceDescription: string;
  serviceType: string;
  serviceOutput?: string;
  image?: string;
}

export function ServicePageSchema({
  serviceName,
  serviceDescription,
  serviceType,
  serviceOutput,
  image = 'https://xxlservices.com/og-image.png'
}: ServicePageSchemaProps) {
  const location = useLocation();
  const pageUrl = `https://xxlservices.com${location.pathname}`;

  useEffect(() => {
    // Remove existing schemas
    document.querySelectorAll('[data-schema-service]').forEach(el => el.remove());

    // Service Schema
    const serviceSchema: WithContext<Service> = {
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": `${pageUrl}#service`,
      "name": serviceName,
      "description": serviceDescription,
      "serviceType": serviceType,
      ...(serviceOutput && { "serviceOutput": serviceOutput }),
      "url": pageUrl,
      "image": image,
      "provider": {
        "@type": "LocalBusiness",
        "@id": "https://xxlservices.com/#organization",
        "name": "XXL IT Services",
        "telephone": "+1-208-328-4500",
        "email": "info@xxlservices.com",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "1624 E Territory Dr",
          "addressLocality": "Meridian",
          "addressRegion": "ID",
          "postalCode": "83646",
          "addressCountry": "US"
        }
      },
      "areaServed": [
        { "@type": "City", "name": "Meridian", "containedInPlace": { "@type": "State", "name": "Idaho" } },
        { "@type": "City", "name": "Boise", "containedInPlace": { "@type": "State", "name": "Idaho" } },
        { "@type": "City", "name": "Nampa", "containedInPlace": { "@type": "State", "name": "Idaho" } },
        { "@type": "City", "name": "Eagle", "containedInPlace": { "@type": "State", "name": "Idaho" } },
        { "@type": "City", "name": "Caldwell", "containedInPlace": { "@type": "State", "name": "Idaho" } },
        { "@type": "City", "name": "Kuna", "containedInPlace": { "@type": "State", "name": "Idaho" } },
        { "@type": "City", "name": "Star", "containedInPlace": { "@type": "State", "name": "Idaho" } },
        { "@type": "City", "name": "Garden City", "containedInPlace": { "@type": "State", "name": "Idaho" } }
      ],
      "availableChannel": {
        "@type": "ServiceChannel",
        "serviceUrl": pageUrl,
        "servicePhone": "+1-208-328-4500",
        "serviceLocation": {
          "@type": "Place",
          "name": "XXL IT Services",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "1624 E Territory Dr",
            "addressLocality": "Meridian",
            "addressRegion": "ID",
            "postalCode": "83646",
            "addressCountry": "US"
          }
        }
      },
      "offers": {
        "@type": "Offer",
        "availability": "https://schema.org/InStock",
        "priceSpecification": {
          "@type": "PriceSpecification",
          "priceCurrency": "USD"
        }
      },
      "termsOfService": "https://xxlservices.com/terms",
      "brand": {
        "@type": "Brand",
        "name": "XXL IT Services"
      }
    };

    // Breadcrumb Schema
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbItems = [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://xxlservices.com"
      }
    ];

    let currentPath = '';
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const name = segment === 'services'
        ? 'IT Services'
        : segment.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

      breadcrumbItems.push({
        "@type": "ListItem",
        "position": index + 2,
        "name": name,
        "item": `https://xxlservices.com${currentPath}`
      });
    });

    const breadcrumbSchema: WithContext<BreadcrumbList> = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbItems
    };

    // Inject schemas
    const serviceScript = document.createElement('script');
    serviceScript.type = 'application/ld+json';
    serviceScript.setAttribute('data-schema-service', 'true');
    serviceScript.textContent = JSON.stringify(serviceSchema);
    document.head.appendChild(serviceScript);

    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.setAttribute('data-schema-service', 'true');
    breadcrumbScript.textContent = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(breadcrumbScript);

    return () => {
      document.querySelectorAll('[data-schema-service]').forEach(el => el.remove());
    };
  }, [location.pathname, serviceName, serviceDescription, serviceType, serviceOutput, image]);

  return null;
}
